import { ZodIssue, ZodSchema } from "zod";
import { disableDraftMode, enableDraftMode } from "@/app/lib/actions/editing";
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { AutosaveState, useAutosave } from "@/app/lib/hooks/useAutosave";
import { PublicationStatus } from "@/app/lib/generated/graphql-types";
import { toast } from "react-toastify";
import { deepMerge, DeepPartial } from "@/app/features/shared/__to_be_integrated/types/forms/deepMerge";
import { EditingState } from "@/app/lib/hooks/use-editor/types/types";
import { defaultEditableState } from "@/app/lib/hooks/use-editor/types/defaults";
import { editableFromGraphQl, inputFromEditable } from "@/app/lib/hooks/use-editor/schema-mapper";
import {
    PublishOptions,
    showPublishResultToast,
} from "@/app/lib/hooks/use-editor/publish-result";

function stripNestedComponentIds<T>(value: T): T {
    if (Array.isArray(value)) {
        return value.map((entry) => stripNestedComponentIds(entry)) as T;
    }

    if (!value || typeof value !== 'object') {
        return value;
    }

    const input = value as Record<string, unknown>;
    const keys = Object.keys(input);
    const output: Record<string, unknown> = {};

    for (const [key, entry] of Object.entries(input)) {
        if (key === 'id' && keys.length > 1) {
            continue;
        }

        output[key] = stripNestedComponentIds(entry);
    }

    return output as T;
}

function normalizeFieldErrors(
    fieldErrors: Record<string, string[] | undefined>
): Record<string, string[]> {
    return Object.fromEntries(
        Object.entries(fieldErrors).filter(([, value]) => Array.isArray(value) && value.length > 0)
    ) as Record<string, string[]>;
}

function extractHasChanges(value: unknown): boolean {
    if (!value || typeof value !== 'object' || !('hasChanges' in value)) {
        return false;
    }

    return Boolean((value as { hasChanges?: unknown }).hasChanges);
}

function extractErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message) return error.message;
    if (typeof error === "string") return error;
    return "Unexpected editor error";
}

function extractZodIssues(error: unknown): unknown[] | undefined {
    if (!error || typeof error !== "object" || !("issues" in error)) return undefined;
    const issues = (error as { issues?: unknown }).issues;
    return Array.isArray(issues) ? issues : undefined;
}


interface UseSingleTypeEditorConfig<TEntity, TEditable, TInput> {
    schema: ZodSchema<TEditable>;
    loadEntity: ({ status }: { status: PublicationStatus }) => Promise<{ data?: TEntity, error?: Error }>;
    prePublish?: (editable: TEditable) => Promise<{ data?: Partial<TEditable>, error?: Error } | null>;
    update: ({ status }: { data: TInput, status: PublicationStatus }) => Promise<{ error?: Error }>;
    toEditable?: (entity: TEntity) => TEditable;
    toInput?: (editable: TEditable) => Promise<TInput> | TInput;
    revalidateTags?: string[];
}

export type ChildEditorSnapshot = {
    hasChanges: boolean;
    autosaveState: AutosaveState | null;
};

export type ChildEditorActions = {
    publish: (options?: PublishOptions) => Promise<boolean> | boolean;
    discardChanges: () => Promise<void> | void;
};

export type UseSingleTypeEditorType<TEditable> = {
    isLoading: boolean;
    autosaveState: AutosaveState | null;
    editable: TEditable | undefined;
    hasChanges: boolean;
    editingState: EditingState;
    error: string | undefined;
    updateEditable: (partialEditable: DeepPartial<TEditable>) => void;
    publish: (options?: PublishOptions) => Promise<boolean>;
    discardChanges: () => void;
    shouldAutosave: boolean;
    hasValidationErrors?: boolean;
    errorForField?: (field: string) => string | null;
    registerChildEditor: (documentId: string, snapshot: ChildEditorSnapshot, actions: ChildEditorActions) => void;
    unregisterChildEditor: (documentId: string) => void;
}

export function useSingleTypeEditor<TEntity, TEditable, TInput>({
    schema,
    loadEntity,
    prePublish,
    update,
    toEditable,
    toInput,
}: UseSingleTypeEditorConfig<TEntity, TEditable, TInput>): UseSingleTypeEditorType<TEditable>  {

    const mapToEditable = useMemo(
        () => toEditable ?? ((entity: TEntity) => editableFromGraphQl(entity, schema)),
        [toEditable, schema]
    );
    const mapToInput = useMemo(
        () => toInput ?? ((editable: TEditable) => inputFromEditable<TEditable, TInput>(editable, schema)),
        [toInput, schema]
    );

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [editable, setEditable] = useState<TEditable | undefined>(undefined)
    const [editingState, setEditingState] = useState<EditingState>(defaultEditableState);
    const [validationIssues, setValidationIssues] = useState<ZodIssue[]>([]);
    const [serverHasChanges, setServerHasChanges] = useState<boolean>(false);
    const [isDirty, setIsDirty] = useState(false);
    const deferredEditable = useDeferredValue(editable);
    const [childEditorSnapshots, setChildEditorSnapshots] = useState<Record<string, ChildEditorSnapshot>>({});
    const childEditorActionsRef = useRef<Map<string, ChildEditorActions>>(new Map());
    const publishInFlightRef = useRef<Promise<boolean> | null>(null);
    const resetSuccessTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const initialEditableRef = useRef<TEditable | undefined>(undefined);
    const validationSignatureRef = useRef<string | undefined>(undefined);
    const shouldAutosave = useMemo(() => {
        return editable !== undefined && Boolean(editingState.isValid) && isDirty;
    }, [editable, editingState.isValid, isDirty]);

    const { autosaveState: autosaveState, error: autosaveError } = useAutosave({
        enabled: shouldAutosave,
        guard: () => editable !== undefined && schema.safeParse(editable).success,
        saveHandler: async () => {
            if (!editable) return;
            const { error } = await update({ data: await mapToInput(editable), status: PublicationStatus.Draft });
            if (error) throw error;
        },
    }, [deferredEditable])

    const updateEditingState = useCallback((partialEditable: Partial<EditingState>) => {
        setEditingState((prev) => deepMerge(prev, partialEditable));
    }, []);

    useEffect(() => {
        if (editable !== undefined && initialEditableRef.current === undefined) {
            initialEditableRef.current = editable;
        }
    }, [editable]);

    // enable draft mode while mounted
    useEffect(() => {
        enableDraftMode();
        return () => {
            disableDraftMode();
        }
    }, []);

    // data loading
    useEffect(() => {
        let cancelled = false;

        (async () => {
            setIsLoading(true);

            try {
                const { data: entity, error } = await loadEntity({ status: PublicationStatus.Draft });

                if (!cancelled) {
                    if (error) {
                        console.error("[useSingleTypeEditor] loadEntity returned error", {
                            error,
                            message: extractErrorMessage(error),
                        });
                        updateEditingState({ error: error.message })
                        return;
                    } else if (!entity) {
                        console.error("[useSingleTypeEditor] loadEntity returned neither data nor error");
                        updateEditingState({ error: "Neither Error nor Data is present, this shouldn't happen!" })
                        return;
                    } else {
                        setServerHasChanges(extractHasChanges(entity));
                        try {
                            const mappedEditable = mapToEditable(entity);
                            initialEditableRef.current = mappedEditable;
                            setIsDirty(false);
                            setEditable(mappedEditable)
                        } catch (mappingError) {
                            console.error("[useSingleTypeEditor] mapToEditable failed", {
                                message: extractErrorMessage(mappingError),
                                zodIssues: extractZodIssues(mappingError),
                                entity,
                            });
                            updateEditingState({ error: extractErrorMessage(mappingError) });
                        }
                    }
                }
            } catch (error) {
                if (!cancelled) {
                    console.error("[useSingleTypeEditor] load effect failed", {
                        message: extractErrorMessage(error),
                        zodIssues: extractZodIssues(error),
                        error,
                    });
                    updateEditingState({ error: extractErrorMessage(error) })
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        })()

        return () => { cancelled = true; };
    }, [loadEntity, mapToEditable, updateEditingState]);

    // live validation
    useEffect(() => {
        if (deferredEditable === undefined) {
            return;
        }

        const validation = schema.safeParse(deferredEditable);
        const signature = validation.success
            ? "valid"
            : JSON.stringify(validation.error.issues.map(({ path, code, message }) => ({ path, code, message })));

        if (validationSignatureRef.current === signature) return;
        validationSignatureRef.current = signature;

        if (!validation.success) {
            const normalizedFieldErrors = normalizeFieldErrors(validation.error.flatten().fieldErrors);

            setValidationIssues(validation.error.issues);
            updateEditingState({
                isValid: false,
                error: validation.error.message,
                fieldErrors: normalizedFieldErrors,
            });
        } else {
            setValidationIssues([]);
            updateEditingState({
                isValid: true,
                fieldErrors: null,
                error: null
            });
        }
    }, [deferredEditable, schema, updateEditingState]);

    const discardChangesBase = useCallback(async () => {
        try {
            const { data, error } = await loadEntity({ status: PublicationStatus.Published });

            if (error) {
                console.error('error while fetch published', error);
                toast.error("Oops, da ist etwas schief gelaufen! Änderungen konnten nicht verworfen werden.")
                return;
            }

            if (!data) {
                toast.error("Keine veröffentlichte Version gefunden. Änderungen konnten nicht verworfen werden.")
                return;
            }

            const publishedEditable = mapToEditable(data);
            const publishedInput = await mapToInput(publishedEditable);
            const resetInput = stripNestedComponentIds(publishedInput);

            const { error: resetDraftError } = await update({
                data: resetInput,
                status: PublicationStatus.Draft,
            });

            if (resetDraftError) {
                console.error('error while resetting draft to published', resetDraftError);
                toast.error("Änderungen konnten serverseitig nicht verworfen werden.")
                return;
            }

            initialEditableRef.current = publishedEditable;
            setIsDirty(false);
            setServerHasChanges(false);
            setEditable(publishedEditable)
            toast.success("Änderungen verworfen.")
        } catch (error) {
            console.error("[useSingleTypeEditor] discardChanges failed", {
                message: extractErrorMessage(error),
                zodIssues: extractZodIssues(error),
                error,
            });
            toast.error("Oops, da ist etwas schief gelaufen! Änderungen konnten nicht verworfen werden.")
        }
    }, [loadEntity, mapToEditable, mapToInput, update]);

    const clearResetSuccessTimeout = useCallback(() => {
        if (resetSuccessTimeoutRef.current) {
            clearTimeout(resetSuccessTimeoutRef.current);
            resetSuccessTimeoutRef.current = null;
        }
    }, []);

    const scheduleResetSuccessState = useCallback(() => {
        clearResetSuccessTimeout();
        resetSuccessTimeoutRef.current = setTimeout(() => {
            updateEditingState({ success: null });
        }, 3000);
    }, [clearResetSuccessTimeout, updateEditingState]);

    useEffect(() => {
        return () => {
            clearResetSuccessTimeout();
        };
    }, [clearResetSuccessTimeout]);

    const publishBase = useCallback(async (): Promise<boolean> => {
        if (!editable) return false;
        let tmpEditable = editable;
        updateEditingState({ isSubmitting: true });

        try {
            let error;
            if (prePublish) {
                const result = await prePublish(editable);
                if (result) {
                    const { data: partialFormData, error: prePublishError } = result;
                    error = prePublishError;

                    if (!prePublishError) tmpEditable  = { ...tmpEditable, ...partialFormData };
                }
            }

            if (!error) {
                const { error: updateError } = await update({ data: await mapToInput(tmpEditable), status: PublicationStatus.Published });
                error = updateError;
            }

            if (error) {
                console.error('error while submitting', JSON.stringify(error, null, 2));
                updateEditingState({
                    success: false,
                    error: error.message,
                    isSubmitting: false
                })
                return false;
            } else {
                initialEditableRef.current = tmpEditable;
                setIsDirty(false);
                setServerHasChanges(false);
                setEditable(tmpEditable);
                updateEditingState({
                    success: true,
                    isSubmitting: false,
                    error: null,
                });
                disableDraftMode();
                return true;
            }
        } catch (error) {
            console.error("[useSingleTypeEditor] publish failed", {
                message: extractErrorMessage(error),
                zodIssues: extractZodIssues(error),
                error,
            });
            updateEditingState({
                success: false,
                error: extractErrorMessage(error),
                isSubmitting: false
            })
            return false;
        } finally {
            scheduleResetSuccessState();
        }
    }, [editable, prePublish, updateEditingState, update, mapToInput, scheduleResetSuccessState]);

    const updateEditable = useCallback((partialEditable: DeepPartial<TEditable>) => {
        setIsDirty(true);
        setEditable(prev => deepMerge(prev, partialEditable));
    }, []);

    const errorForField = useCallback((field: string): string | null => {
        const messages = validationIssues
            .filter((issue) => issue.path.join('.') === field)
            .map((issue) => issue.message);

        return messages.length > 0 ? messages.join('\n') : null;
    }, [validationIssues]);

    const registerChildEditor = useCallback((documentId: string, snapshot: ChildEditorSnapshot, actions: ChildEditorActions) => {
        childEditorActionsRef.current.set(documentId, actions);
        setChildEditorSnapshots((prev) => {
            const current = prev[documentId];
            if (
                current
                && current.hasChanges === snapshot.hasChanges
                && current.autosaveState === snapshot.autosaveState
            ) {
                return prev;
            }

            return {
                ...prev,
                [documentId]: snapshot,
            };
        });
    }, []);

    const unregisterChildEditor = useCallback((documentId: string) => {
        childEditorActionsRef.current.delete(documentId);
        setChildEditorSnapshots((prev) => {
            if (!(documentId in prev)) {
                return prev;
            }

            const next = { ...prev };
            delete next[documentId];
            return next;
        });
    }, []);

    const aggregatedHasChanges = useMemo(() => {
        const hasChildChanges = Object.values(childEditorSnapshots).some((snapshot) => snapshot.hasChanges);
        return serverHasChanges || isDirty || hasChildChanges;
    }, [childEditorSnapshots, isDirty, serverHasChanges]);

    const aggregatedAutosaveState = useMemo<AutosaveState>(() => {
        const states = [autosaveState, ...Object.values(childEditorSnapshots).map((snapshot) => snapshot.autosaveState)];
        if (states.some((state) => state === 'running')) return 'running';
        if (states.some((state) => state === 'error')) return 'error';
        if (states.some((state) => state === 'success')) return 'success';
        return null;
    }, [autosaveState, childEditorSnapshots]);

    const publish = useCallback((options: PublishOptions = {}): Promise<boolean> => {
        if (publishInFlightRef.current) {
            return publishInFlightRef.current;
        }

        const operation = (async () => {
            const changedChildActions = Array.from(childEditorActionsRef.current.entries())
                .filter(([documentId]) => childEditorSnapshots[documentId]?.hasChanges)
                .map(([, actions]) => actions);
            const childResults = await Promise.all(
                changedChildActions.map((actions) => actions.publish({ notify: false }))
            );
            const selfResult = serverHasChanges || isDirty
                ? await Promise.resolve(publishBase())
                : true;
            const selfSucceeded = typeof selfResult === 'boolean' ? selfResult : true;
            const succeeded = childResults.every(Boolean) && selfSucceeded;

            if (options.notify !== false) {
                showPublishResultToast(succeeded);
            }

            return succeeded;
        })();

        publishInFlightRef.current = operation;
        const clearInFlight = () => {
            if (publishInFlightRef.current === operation) {
                publishInFlightRef.current = null;
            }
        };
        void operation.then(clearInFlight, clearInFlight);

        return operation;
    }, [childEditorSnapshots, isDirty, publishBase, serverHasChanges]);

    const discardChanges = useCallback(async (): Promise<void> => {
        await Promise.all(
            Array.from(childEditorActionsRef.current.values()).map((actions) => actions.discardChanges())
        );
        await Promise.resolve(discardChangesBase());
    }, [discardChangesBase]);

    return {
        isLoading,
        editable,
        updateEditable,
        editingState,
        autosaveState: aggregatedAutosaveState,
        error: autosaveError || editingState.error || undefined,
        hasChanges: aggregatedHasChanges,
        publish,
        discardChanges,
        shouldAutosave,
        hasValidationErrors: validationIssues.length > 0,
        errorForField,
        registerChildEditor,
        unregisterChildEditor,
    }
}
